import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import styles from "./calculator.module.scss";
import webStyles from "../../features/calculator/calculatorWeb.module.scss";
import { ReactComponent as CheckIcon } from "../../features/calculator/images/check.svg";
import useScreenSize from "../../hooks/useScreenSize";

const UnitSelector = ({ visible, units, selected, onSelect, onClose, isWebLayout = false }) => {
  const { isDesktop } = useScreenSize();
  const dropdownRef = useRef(null);  
  
  useEffect(() => {
    if (!visible || !isWebLayout || !isDesktop) return;
    
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, isWebLayout, isDesktop, onClose]);
  
  if (!visible) return null;

  // For web layout on tablet/desktop: use inline dropdown (new web design)
  if (isWebLayout && isDesktop) {
    return (
      <div 
        ref={dropdownRef}
        className={webStyles.unitSelectorDropdown} 
        data-testid="unit-selector-dropdown"
      >
        {units.map((unit) => (
          <button
            key={unit.value}
            className={webStyles.unitSelectorDropdownItem}
            onClick={() => {
              onSelect(unit.value);
              onClose();
            }}
            type="button"
            tabIndex={0}
          >
            {/* Always render checkmark container for consistent alignment */}
            <span className={webStyles.unitSelectorDropdownCheck}>
              {selected === unit.value && <CheckIcon />}
            </span>
            <span className={webStyles.unitSelectorDropdownLabel}>{unit.label}</span>
          </button>
        ))}
      </div>
    );
  }

  // For mobile or PWA: use modal overlay (existing design)
  const modalContent = (
    <div 
      className={styles.unitSelectorBackdrop} 
      data-testid="unit-selector"
      onClick={onClose}
    >
      <div 
        className={styles.unitSelectorModal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.unitSelectorHeader}>
          <button
            className={styles.unitSelectorCloseBtn}
            onClick={onClose}
            aria-label="Close unit selector"
            type="button"
          >
            <i className="tg-icons-Cross"></i>
          </button>
        </div>
        <div className={styles.unitSelectorList}>
          {units.map((unit) => (
            <button
              key={unit.value}
              className={styles.unitSelectorRow}
              onClick={() => {
                onSelect(unit.value);
                onClose();
              }}
              type="button"
              tabIndex={0}
            >
              <span className={styles.unitSelectorLabel}>{unit.label}</span>
              {selected === unit.value && (
                <span className={styles.unitSelectorCheck}>
                  <CheckIcon />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  
  return createPortal(modalContent, document.body);
};

UnitSelector.propTypes = {
  visible: PropTypes.bool,
  units: PropTypes.array.isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isWebLayout: PropTypes.bool,
};

export default UnitSelector;
